import { createContext, useEffect, useState, ReactElement } from 'react';
import getConfig from 'next/config';
import { useRouter } from 'next/router';

import { bugsnag } from 'shared/utils/services/bugsnag';
import { FeatureFlags } from 'featureFlags/constants';
import { useCurrency } from 'shared/hooks/useCurrency';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { setUpMParticle } from 'tracking/utils/setUpMParticle';

interface IMParticleProvider {
  children: ReactElement;
  clientDedupID: string;
}

export const MParticleContext = createContext(null);

export const MParticleProvider = ({ children, clientDedupID }: IMParticleProvider) => {
  const isMParticleEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_MPARTICLE);
  const [isMParticleReady, setIsMParticleReady] = useState(false);
  const [isFirstPageLoad, setIsFirstPageLoad] = useState(true);
  const router = useRouter();
  const { selectedCurrencyCode } = useCurrency();
  const isNewSearchPageEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND);

  // this useEffect call forces this logic to run on the client
  useEffect(() => {
    if (isMParticleEnabled) {
      const {
        publicRuntimeConfig: { MPARTICLE_API_KEY: mParticleApiKey, RELEASE_ENV: releaseEnv },
      } = getConfig();

      try {
        setUpMParticle(mParticleApiKey, releaseEnv);
        setIsMParticleReady(true);

        window.mParticle.ready(() => {
          window.mParticle.eCommerce.setCurrencyCode(selectedCurrencyCode);
        });
      } catch (error) {
        const errorMessage = (error as Error).message;
        bugsnag(`There was an issue setting up mParticle: ${errorMessage}`, {
          context: 'MParticleProvider',
        });
      }
    }
  }, [isMParticleEnabled]);

  useEffect(() => {
    /* eslint-disable camelcase */
    const pageView = (url) => {
      const {
        query: { utm_campaign, utm_content, utm_medium, utm_source, utm_term },
      } = router;
      sendTrackingEvent('PAGE_VIEW', {
        location: url,
        client_dedup_id: clientDedupID,
        ...(utm_campaign && { utm_campaign: utm_campaign as string }),
        ...(utm_content && { utm_content: utm_content as string }),
        ...(utm_medium && { utm_medium: utm_medium as string }),
        ...(utm_source && { utm_source: utm_source as string }),
        ...(utm_term && { utm_term: utm_term as string }),
        service_name: isNewSearchPageEnabled ? 'consumer_search' : 'constructor',
      });
    };
    /* eslint-enable camelcase */

    if (!isMParticleEnabled) return null;

    if (isFirstPageLoad) {
      pageView(router?.asPath);

      setIsFirstPageLoad(false);
    }

    router.events.on('routeChangeComplete', pageView);

    return () => {
      router.events.off('routeChangeComplete', pageView);
    };
  }, [router.events, clientDedupID]);

  return (
    <MParticleContext.Provider value={{ isMParticleReady }}>{children}</MParticleContext.Provider>
  );
};
